import clsx from 'clsx'
import type { HTMLAttributes, ReactNode } from 'react'

export type BadgeVariant = 'gray' | 'blue' | 'green' | 'amber' | 'red'
export type QcSeverity = 'pass' | 'warn' | 'fail'

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /**
   * QC-aware severity. When provided it overrides `variant`.
   * - pass  -> green
   * - warn  -> amber
   * - fail  -> red (only used for hard failures per friendly tone rules)
   */
  qcSeverity?: QcSeverity | null
  variant?: BadgeVariant
  children: ReactNode
}

const variantClass: Record<BadgeVariant, string> = {
  gray: 'border-[var(--border)] bg-[var(--bg-3)] text-[var(--text-2)]',
  blue: 'border-[var(--accent)]/40 bg-[var(--accent)]/15 text-[var(--accent)]',
  green: 'border-[var(--green)]/40 bg-[var(--green)]/15 text-[var(--green)]',
  amber: 'border-[var(--amber)]/40 bg-[var(--amber)]/15 text-[var(--amber)]',
  red: 'border-[var(--red)]/40 bg-[var(--red)]/15 text-[var(--red)]',
}

const severityToVariant: Record<QcSeverity, BadgeVariant> = {
  pass: 'green',
  warn: 'amber',
  fail: 'red',
}

export function Badge({ qcSeverity, variant = 'gray', className, children, ...rest }: BadgeProps) {
  const resolved = qcSeverity ? severityToVariant[qcSeverity] : variant
  return (
    <span
      data-qc-severity={qcSeverity ?? undefined}
      className={clsx(
        'inline-flex items-center gap-1 rounded-[var(--radius-sm)] border px-2 py-0.5 text-[12px] font-medium',
        variantClass[resolved],
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  )
}
